import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/store';
import { selectSite } from 'features/topbar/selectedSiteSlice';
import { UserSite, useGetSitesQuery } from 'app/apis/queryApi';
import { FC, useMemo, useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import Loading from 'common/components/loading/loading';
import { useFilteredValues } from 'common/hooks/filteredValues';

/* TODO: Refactor
  See ticket: https://ruleranalytics.atlassian.net/browse/OV-837
*/

const SitePicker: FC = () => {
  const dispatch = useDispatch();
  const { siteLabel } = useSelector((state: RootState) => state.selectedSite);
  const { isBlurActive } = useSelector((state: RootState) => state.blur);

  const [searchTerm, setSearchTerm] = useState('');
  const [valueForInput, setValueForInput] = useState(siteLabel);
  const [openDropDown, setOpenDropDown] = useState(false);
  const sitePickerRef = useRef(null);

  const { data: siteData, isLoading, isFetching } = useGetSitesQuery();

  const filteredSites = useFilteredValues(siteData, searchTerm, (site, term) =>
    site.label.toLowerCase().includes(term)
  );

  useClickAway(sitePickerRef, () => {
    setValueForInput(siteLabel);
    setOpenDropDown(false);
  });

  if (!siteLabel) {
    return <></>;
  }

  if (siteData && siteData.length === 1) {
    return (
      <div className="text-primary-600 text-xl p-1 pr-0 pl-0 border-b-2 border-secondary-100 rounded border-opacity-50 whitespace-nowrap">
        {siteData[0].label.endsWith('/')
          ? siteData[0].label.slice(0, -1)
          : siteData[0].label}
      </div>
    );
  }

  if (isBlurActive) {
    return <></>;
  }

  if (!isLoading && !isFetching) {
    if (!siteData || siteData.length === 0) {
      return (
        <div className="min-w-max w-64" ref={sitePickerRef}>
          <div className="flex justify-between align-middle border border-neutral-200 rounded">
            <p className="p-1 pl-4">No sites found</p>
          </div>
        </div>
      );
    }

    return (
      <div className="min-w-max w-64" ref={sitePickerRef}>
        <>
          <div
            className="flex justify-between align-middle border border-neutral-200 rounded"
            onClick={() => {
              setOpenDropDown(!openDropDown);
            }}
          >
            <div className="relative">
              <input
                className="p-3 pl-4 rounded border-0"
                type="search"
                id="site-picker"
                name="site-picker"
                value={valueForInput}
                aria-haspopup="true"
                onChange={(e) => {
                  setValueForInput(e.target.value);
                  setSearchTerm(e.target.value);
                  if (!openDropDown) {
                    setOpenDropDown(true);
                  }
                }}
                onClick={(e) => {
                  setValueForInput(searchTerm);
                  if (!openDropDown) {
                    setOpenDropDown(true);
                  }
                }}
              />
              {searchTerm ? (
                <button
                  className="absolute inset-y-0 right-0 flex items-center text-navy pr-2.5 bg-transparent z-10"
                  onClick={(e) => {
                    e.preventDefault();
                    setSearchTerm('');
                  }}
                >
                  <i className="ra-icons-generic-cross-1 text-primary-700 flex"></i>
                </button>
              ) : null}
            </div>
            <i className="ra-icons-generic-down-arrow pl-1 pr-4 pt-4 pb-1 text-bottom text-xs"></i>
          </div>
          {openDropDown ? (
            <div className="bg-white absolute z-40 drop-shadow-xl overflow-y-auto mt-2 border rounded border-off-navy md:max-h-96 md:max-w-md">
              <div className="bg-white box-content grid-cols-1 divide-y divide-off-navy md:max-w-md w-full">
                {filteredSites.map((site) => {
                  return (
                    <div
                      className={`px-3 m-0 p-1 cursor-pointer border-0 first:rounded-t last:rounded-b transition-all ${
                        site.label === siteLabel
                          ? 'bg-neutral-100 text-neutral-500'
                          : 'bg-white hover:bg-neutral-200'
                      }`}
                      key={`site-picker-item-${site.id}`}
                      onClick={() => {
                        dispatch(selectSite(site));
                        setValueForInput(site.label);
                      }}
                    >
                      <button>{site.label}</button>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </>
      </div>
    );
  } else {
    return (
      <div className="min-w-max w-64" ref={sitePickerRef}>
        <div className="flex justify-between align-middle border border-off-navy rounded">
          <div className="min-h-fit p-3 pl-4 w-full">
            <Loading />
          </div>
          <i className="ra-icons-generic-down-arrow pl-1 pr-4 pt-2 pb-1 text-bottom text-xs"></i>
        </div>
      </div>
    );
  }
};

export default SitePicker;
